import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/hooks/useAuth';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronRight, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

// ── Types ─────────────────────────────────────────────────────────────────────

interface LookupItem { value: string; label: string; code: string; }
interface ServiceType { id: string; code: string; name: string; name_translations?: { ar?: string; en?: string }; }
interface RouteTemplate { id: string; name: string; }

interface PricingRoute {
  id: string;
  service_type?: { id: string; code: string; name: string };
  template?: { id: string; name: string };
  from_country_id?: string;
  from_state_id?: string;
  from_city_id?: string;
  to_country_id?: string;
  to_state_id?: string;
  to_city_id?: string;
  price_per_kg: number;
  minimum_charge: number;
  currency: string;
  priority: number;
  is_active: boolean;
}

// ── Default form ──────────────────────────────────────────────────────────────

function defaultForm() {
  return {
    template_id: '',
    service_type_id: '',
    priority: '0',
    from_country_id: '',
    from_state_id: '',
    from_city_id: '',
    to_country_id: '',
    to_state_id: '',
    to_city_id: '',
    weight_min_kg: '',
    weight_max_kg: '',
    price_per_kg: '0',
    minimum_charge: '',
    currency: 'SAR',
    is_active: true,
  };
}
type FormState = ReturnType<typeof defaultForm>;

// ── Location select group ─────────────────────────────────────────────────────

function LocationSelect({
  title,
  countries, states, cities,
  country_id, state_id, city_id,
  onCountry, onState, onCity,
}: {
  title: string;
  countries: LookupItem[];
  states: LookupItem[];
  cities: LookupItem[];
  country_id: string;
  state_id: string;
  city_id: string;
  onCountry: (v: string) => void;
  onState: (v: string) => void;
  onCity: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label className="font-semibold text-sm">{title}</Label>
      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">الدولة</Label>
          <Select value={country_id} onValueChange={onCountry}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="أي دولة" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_any">أي دولة</SelectItem>
              {countries.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">المنطقة</Label>
          <Select value={state_id} onValueChange={onState}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="أي منطقة" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_any">أي منطقة</SelectItem>
              {states.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">المدينة</Label>
          <Select value={city_id} onValueChange={onCity}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="أي مدينة" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_any">أي مدينة</SelectItem>
              {cities.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function PricingRoutesPage() {
  const navigate = useNavigate();
  const { get, post, delete: del, loading } = useApi();
  const { user } = useAuth();

  const carrierId = user?.carrier_id;

  const [routes, setRoutes] = useState<PricingRoute[]>([]);
  const [templates, setTemplates] = useState<RouteTemplate[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [countries, setCountries] = useState<LookupItem[]>([]);
  const [states, setStates] = useState<LookupItem[]>([]);
  const [cities, setCities] = useState<LookupItem[]>([]);

  const [hasFetched, setHasFetched] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<FormState>(defaultForm());

  // ── ID → name maps for list display ──
  const countryMap = Object.fromEntries(countries.map((c) => [c.value, c.label]));
  const stateMap = Object.fromEntries(states.map((s) => [s.value, s.label]));
  const cityMap = Object.fromEntries(cities.map((c) => [c.value, c.label]));

  const fetchRoutes = useCallback(async () => {
    if (!carrierId) return;
    try {
      const res: any = await get(`/api/v1/carriers/${carrierId}/pricing-routes`);
      setRoutes(Array.isArray(res) ? res : res?.list ?? res?.data ?? []);
    } catch {
      toast.error('تعذر تحميل مسارات التسعير');
    } finally {
      setHasFetched(true);
    }
  }, [carrierId, get]);

  useEffect(() => {
    if (!carrierId) return;

    fetchRoutes();

    get(`/api/v1/carriers/${carrierId}/route-templates`)
      .then((res: any) => setTemplates(Array.isArray(res) ? res : res?.list ?? res?.data ?? []))
      .catch(() => {});

    get('/api/v1/settings/service-types')
      .then((res: any) => setServiceTypes(Array.isArray(res) ? res : res?.data ?? []))
      .catch(() => {});

    get('/api/v1/lookups/countries?lang=ar')
      .then((res: any) => setCountries(res?.items ?? res?.data?.items ?? []))
      .catch(() => {});

    get('/api/v1/lookups/states?lang=ar')
      .then((res: any) => setStates(res?.items ?? res?.data?.items ?? []))
      .catch(() => {});

    get('/api/v1/settings/cities?lang=ar')
      .then((res: any) => {
        const list = Array.isArray(res) ? res : res?.data ?? res?.list ?? [];
        setCities(list.map((c: any) => ({
          value: c.id ?? c.value,
          label: c.name_translations?.ar ?? c.name ?? c.label ?? c.id,
          code: c.code ?? '',
        })));
      })
      .catch(() => {});
  }, [carrierId]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const clearGeo = (side: 'from' | 'to') => {
    setField(`${side}_country_id`, '');
    setField(`${side}_state_id`, '');
    setField(`${side}_city_id`, '');
  };

  const handleDelete = async (id: string) => {
    if (!carrierId) return;
    try {
      await del(`/api/v1/carriers/${carrierId}/pricing-routes/${id}`);
      toast.success('تم حذف مسار التسعير');
      fetchRoutes();
    } catch {
      toast.error('تعذر حذف مسار التسعير');
    }
  };

  const handleSubmit = async () => {
    if (!carrierId) return;
    if (!form.template_id) { toast.error('القالب مطلوب'); return; }
    if (!form.service_type_id) { toast.error('نوع الخدمة مطلوب'); return; }
    if (!form.minimum_charge) { toast.error('الحد الأدنى للرسوم مطلوب'); return; }

    const resolve = (v: string) => (v && v !== '_any' ? v : undefined);

    setSubmitting(true);
    try {
      await post(`/api/v1/carriers/${carrierId}/pricing-routes`, {
        template_id: form.template_id,
        service_type_code: form.service_type_id,
        priority: parseInt(form.priority) || 0,
        from_country_id: resolve(form.from_country_id),
        from_state_id: resolve(form.from_state_id),
        from_city_id: resolve(form.from_city_id),
        to_country_id: resolve(form.to_country_id),
        to_state_id: resolve(form.to_state_id),
        to_city_id: resolve(form.to_city_id),
        weight_min_kg: form.weight_min_kg ? parseFloat(form.weight_min_kg) : undefined,
        weight_max_kg: form.weight_max_kg ? parseFloat(form.weight_max_kg) : undefined,
        price_per_kg: parseFloat(form.price_per_kg) || 0,
        minimum_charge: parseFloat(form.minimum_charge),
        currency: form.currency || 'SAR',
        is_active: form.is_active,
      });
      toast.success('تم إنشاء مسار التسعير بنجاح');
      setShowCreate(false);
      setForm(defaultForm());
      fetchRoutes();
    } catch {
      toast.error('تعذر إنشاء مسار التسعير');
    } finally {
      setSubmitting(false);
    }
  };

  const geoLabel = (countryId?: string, stateId?: string, cityId?: string) => {
    const parts = [
      cityId ? cityMap[cityId] : null,
      stateId ? stateMap[stateId] : null,
      countryId ? countryMap[countryId] : null,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' / ') : 'أي موقع';
  };

  if (!carrierId) return null;

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <PageContainer>
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronRight className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">مسارات التسعير</h1>
              <p className="text-sm text-muted-foreground">إدارة قواعد تسعير مسارات الشحن</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            إضافة مسار تسعير
          </Button>
        </div>

        {(!hasFetched || loading) ? (
          <LoadingSkeleton type="card" rows={4} />
        ) : routes.length === 0 ? (
          <EmptyState
            icon={<DollarSign className="h-16 w-16" />}
            title="لا توجد مسارات تسعير"
            description="أضف مسار تسعير لتحديد رسوم الشحن حسب المناطق والقوالب"
            action={{ label: 'إضافة مسار تسعير', onClick: () => setShowCreate(true) }}
          />
        ) : (
          <div className="space-y-3">
            {routes.map((route) => (
              <Card key={route.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        {route.service_type && (
                          <Badge variant="secondary" className="text-xs">
                            {route.service_type.name ?? route.service_type.code}
                          </Badge>
                        )}
                        {route.template && (
                          <Badge variant="outline" className="text-xs">
                            {route.template.name}
                          </Badge>
                        )}
                        <Badge variant={route.is_active ? 'default' : 'outline'} className="text-xs">
                          {route.is_active ? 'نشط' : 'غير نشط'}
                        </Badge>
                        <span className="text-xs text-muted-foreground">أولوية: {route.priority}</span>
                      </div>

                      <div className="flex items-center gap-2 text-sm flex-wrap">
                        <span className="text-muted-foreground text-xs">من</span>
                        <span className="font-medium">
                          {geoLabel(route.from_country_id, route.from_state_id, route.from_city_id)}
                        </span>
                        <span className="text-muted-foreground">←</span>
                        <span className="text-muted-foreground text-xs">إلى</span>
                        <span className="font-medium">
                          {geoLabel(route.to_country_id, route.to_state_id, route.to_city_id)}
                        </span>
                      </div>

                      <div className="flex gap-4 text-sm">
                        <span>
                          <span className="text-muted-foreground">سعر/كجم: </span>
                          <span className="font-semibold text-primary">{route.price_per_kg}</span>
                        </span>
                        <span>
                          <span className="text-muted-foreground">الحد الأدنى: </span>
                          <span className="font-semibold">{route.minimum_charge}</span>
                        </span>
                      </div>
                    </div>

                    <Button
                      variant="ghost" size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive shrink-0"
                      onClick={() => handleDelete(route.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* ── Create Dialog ─────────────────────────────────────────────── */}
        <Dialog open={showCreate} onOpenChange={(open) => { if (!open) setForm(defaultForm()); setShowCreate(open); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
            <DialogHeader>
              <DialogTitle>إضافة مسار تسعير جديد</DialogTitle>
            </DialogHeader>

            <div className="space-y-5 py-2">
              {/* Template — required */}
              <div className="space-y-2">
                <Label>القالب <span className="text-destructive">*</span></Label>
                <Select value={form.template_id} onValueChange={(v) => setField('template_id', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="اختر القالب" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Service type — required */}
              <div className="space-y-2">
                <Label>نوع الخدمة <span className="text-destructive">*</span></Label>
                <Select value={form.service_type_id} onValueChange={(v) => setField('service_type_id', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="اختر نوع الخدمة" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypes.map((st) => (
                      <SelectItem key={st.id} value={st.code}>
                        {st.name_translations?.ar ?? st.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Priority */}
              <div className="space-y-2">
                <Label>الأولوية <span className="text-xs text-muted-foreground">(رقم أعلى = أولوية أعلى)</span></Label>
                <Input type="number" min="0" value={form.priority} onChange={(e) => setField('priority', e.target.value)} className="w-32" />
              </div>

              {/* From */}
              <LocationSelect
                title="من (المصدر)"
                countries={countries} states={states} cities={cities}
                country_id={form.from_country_id} state_id={form.from_state_id} city_id={form.from_city_id}
                onCountry={(v) => { setField('from_country_id', v === '_any' ? '' : v); setField('from_state_id', ''); setField('from_city_id', ''); }}
                onState={(v) => { setField('from_state_id', v === '_any' ? '' : v); setField('from_city_id', ''); }}
                onCity={(v) => setField('from_city_id', v === '_any' ? '' : v)}
              />

              {/* To */}
              <LocationSelect
                title="إلى (الوجهة)"
                countries={countries} states={states} cities={cities}
                country_id={form.to_country_id} state_id={form.to_state_id} city_id={form.to_city_id}
                onCountry={(v) => { setField('to_country_id', v === '_any' ? '' : v); setField('to_state_id', ''); setField('to_city_id', ''); }}
                onState={(v) => { setField('to_state_id', v === '_any' ? '' : v); setField('to_city_id', ''); }}
                onCity={(v) => setField('to_city_id', v === '_any' ? '' : v)}
              />

              {/* Weight brackets */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>الوزن الأدنى (كجم)</Label>
                  <Input type="number" min="0" step="0.1" placeholder="اختياري" value={form.weight_min_kg} onChange={(e) => setField('weight_min_kg', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>الوزن الأقصى (كجم)</Label>
                  <Input type="number" min="0" step="0.1" placeholder="اختياري" value={form.weight_max_kg} onChange={(e) => setField('weight_max_kg', e.target.value)} />
                </div>
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>سعر/كجم</Label>
                  <Input type="number" min="0" step="0.01" value={form.price_per_kg} onChange={(e) => setField('price_per_kg', e.target.value)} placeholder="0.00" />
                </div>
                <div className="space-y-2">
                  <Label>الحد الأدنى <span className="text-destructive">*</span></Label>
                  <Input type="number" min="0" step="0.01" value={form.minimum_charge} onChange={(e) => setField('minimum_charge', e.target.value)} placeholder="0.00" />
                </div>
                <div className="space-y-2">
                  <Label>العملة</Label>
                  <Input value={form.currency} onChange={(e) => setField('currency', e.target.value)} maxLength={3} dir="ltr" />
                </div>
              </div>

              {/* Active */}
              <div className="flex items-center gap-2">
                <Checkbox id="route_active" checked={form.is_active} onCheckedChange={(v) => setField('is_active', !!v)} />
                <Label htmlFor="route_active" className="cursor-pointer">نشط</Label>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => { setShowCreate(false); setForm(defaultForm()); }} disabled={submitting}>
                إلغاء
              </Button>
              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'جارٍ الحفظ...' : 'حفظ المسار'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageContainer>
    </div>
  );
}
